/**
 * The locales the shell ships. A leaf module on purpose — the global state and
 * the i18n runtime both import it, and neither may import the other.
 *
 * D4: South Africa is the first POPULATED locale, never the assumed one —
 * every string goes through the runtime from the first screen, and adding a
 * locale is one message file plus one entry here.
 */
export const SUPPORTED_LOCALES = ["en-ZA", "af-ZA"] as const;

export type ShellLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: ShellLocale = "en-ZA";

/** Endonyms — a language is named in itself, so a visitor can find their own. */
export const LOCALE_NAMES: Readonly<Record<ShellLocale, string>> = {
  "en-ZA": "English",
  "af-ZA": "Afrikaans",
};

export function isShellLocale(value: unknown): value is ShellLocale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Best supported locale for a BCP-47 tag: an exact match first, then the
 * language subtag ("af" → "af-ZA", "en-GB" → "en-ZA"). Null when nothing fits.
 */
export function matchLocale(tag: string | null | undefined): ShellLocale | null {
  if (!tag) return null;
  const wanted = tag.trim().toLowerCase();
  if (!wanted) return null;
  const exact = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === wanted);
  if (exact) return exact;
  const language = wanted.split(/[-_]/)[0] ?? "";
  return SUPPORTED_LOCALES.find((locale) => locale.toLowerCase().split("-")[0] === language) ?? null;
}
